import i18n from '@/assets/i18n/i18n'
import usageApi from '@/api/usageApi'
import { usageErrorText } from './usage'

// Account > Settings, the Usage panels: the start time tolerance and the account's episode patterns

const state = {
    // GET /publishing/usage/settings/tolerance; the panel edits generalSeconds and the compared society's seconds
    usageTolerance: null,
    usageToleranceSaved: null,

    usageEpisodePatterns: { custom: [], system: [] },
    // { show, pattern: the fields, errors: field => text, saving }
    usageEpisodePatternDialog: { show: false },
    // The pattern the deletion dialog deletes
    usageEpisodePattern: null
}

const getters = {
    getUsageTolerance                   (state) { return state.usageTolerance },
    getUsageEpisodePatterns             (state) { return state.usageEpisodePatterns },
    getUsageEpisodePatternDialog        (state) { return state.usageEpisodePatternDialog },
    getUsageEpisodePattern              (state) { return state.usageEpisodePattern },
    getUsageToleranceChanged            (state) { return JSON.stringify(state.usageTolerance) !== JSON.stringify(state.usageToleranceSaved) }
}

const mutations = {
    setUsageTolerance:                  (state, tolerance) => { state.usageTolerance = tolerance },
    setUsageToleranceSaved:             (state, tolerance) => { state.usageToleranceSaved = tolerance },
    setUsageEpisodePatterns:            (state, patterns) => { state.usageEpisodePatterns = patterns },
    setUsageEpisodePatternDialog:       (state, dialog) => { state.usageEpisodePatternDialog = dialog },
    setUsageEpisodePattern:             (state, pattern) => { state.usageEpisodePattern = pattern }
}

const actions = {
    async fetchUsageSettings({ commit }) {
        const [tolerance, patterns] = await Promise.all([usageApi.tolerance(), usageApi.episodePatterns()])
        if (tolerance.ok) {
            commit('setUsageTolerance', JSON.parse(JSON.stringify(tolerance.data)))
            commit('setUsageToleranceSaved', JSON.parse(JSON.stringify(tolerance.data)))
        }
        if (patterns.ok) commit('setUsageEpisodePatterns', patterns.data)
        const failed = [tolerance, patterns].find(r => !r.ok)
        if (failed) commit('setSnackbar', { type: 'error', msg: usageErrorText(failed.error) })
    },

    // Saved with the page's Save button, only when changed; the page's own message reports success
    async saveUsageTolerance({ commit, getters }) {
        const tolerance = getters.getUsageTolerance
        if (!tolerance || !getters.getUsageToleranceChanged) return true
        const seconds = value => (value === null || value === undefined || value === '') ? null : Number(value)
        const societies = {}
        Object.entries(tolerance.societies || {}).forEach(([code, value]) => {
            if (seconds(value) !== null) societies[code] = seconds(value)
        })
        const result = await usageApi.saveTolerance({ generalSeconds: seconds(tolerance.generalSeconds), societies })
        if (!result.ok) {
            commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            return false
        }
        commit('setUsageTolerance', JSON.parse(JSON.stringify(result.data)))
        commit('setUsageToleranceSaved', JSON.parse(JSON.stringify(result.data)))
        return true
    },

    openUsageEpisodePatternDialog({ commit }, pattern) {
        commit('setUsageEpisodePatternDialog', {
            show: true,
            errors: {},
            saving: false,
            pattern: pattern
                ? { ...pattern }
                : { id: null, pattern: '', example: '', numbering: 'absolute', priority: 100, dataType: null, description: '', isActive: true }
        })
    },

    async saveUsageEpisodePattern({ commit, dispatch, getters }) {
        const dialog = getters.getUsageEpisodePatternDialog
        const p = dialog.pattern
        const body = {
            pattern: p.pattern,
            example: p.example,
            numbering: p.numbering,
            priority: Number(p.priority),
            dataType: p.dataType || null,
            description: p.description || '',
            isActive: !!p.isActive
        }
        commit('setUsageEpisodePatternDialog', { ...dialog, saving: true, errors: {} })
        const result = p.id ? await usageApi.updateEpisodePattern(p.id, body) : await usageApi.createEpisodePattern(body)
        if (!result.ok) {
            // Save-time validation names the field to fix; shown at that field
            const errors = result.error.details.fields || {}
            commit('setUsageEpisodePatternDialog', { ...dialog, saving: false, errors })
            if (Object.keys(errors).length === 0) commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            return
        }
        commit('setUsageEpisodePatternDialog', { show: false })
        commit('setSnackbar', { type: 'success', msg: i18n.t(p.id ? 'lang_ACCOUNT_SETTINGS_SNACKBAR_SUCCESSFULLY_SAVED' : 'lang_GENERIC_SNACKBAR_ADDING_WAS_SUCCESSFUL') })
        dispatch('fetchUsageEpisodePatterns')
    },

    // Switching a pattern on or off saves at once, as the pattern stands
    async toggleUsageEpisodePattern({ commit, dispatch }, pattern) {
        const result = await usageApi.updateEpisodePattern(pattern.id, {
            pattern: pattern.pattern, example: pattern.example, numbering: pattern.numbering, priority: pattern.priority,
            dataType: pattern.dataType, description: pattern.description, isActive: !pattern.isActive
        })
        if (!result.ok) commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
        dispatch('fetchUsageEpisodePatterns')
    },

    async fetchUsageEpisodePatterns({ commit }) {
        const result = await usageApi.episodePatterns()
        if (result.ok) commit('setUsageEpisodePatterns', result.data)
    },

    // Called by the shared DeletionDialog (context "UsageEpisodePattern")
    async deleteUsageEpisodePattern({ commit, dispatch, getters }) {
        const pattern = getters.getUsageEpisodePattern
        if (!pattern) return
        const result = await usageApi.deleteEpisodePattern(pattern.id)
        commit('setUsageEpisodePattern', null)
        commit('setSnackbar', result.ok
            ? { type: 'success', msg: i18n.t('lang_GENERIC_SNACKBAR_ENTRY_DELETED') }
            : { type: 'error', msg: usageErrorText(result.error) })
        dispatch('fetchUsageEpisodePatterns')
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
